/**
 * The invite code, rescued from the URL before anything can throw it away.
 *
 * An invite link is `/join?code=ABC123`, and by the time the join screen is on
 * screen that code has been lost twice over:
 *
 *  - The `/join` route redirects to `/tasks` on the router's *first* pass —
 *    before anyone has signed in, and without carrying the search string.
 *  - The whole email-and-one-time-code flow happens after that, so the screen
 *    that wants the code renders minutes later at a different URL.
 *
 * So the link worked exactly as well as no link: the code still had to be typed
 * in by hand, which is the one thing it existed to avoid.
 *
 * Read once at module load — before `RouterProvider` mounts, so before the
 * redirect — and kept in `sessionStorage` rather than in a variable, because on
 * iOS the person leaves for Mail to fetch their one-time code and Safari is
 * free to discard and reload the tab while they are gone. Session storage
 * survives that reload; a module variable does not, and the URL it would reload
 * from no longer has the code in it.
 *
 * Tab-scoped and spent on first use, so a code never outlives the join it was
 * for. Losing the redirect's stripping of the query string would be the wrong
 * fix for the same reason: a live code has no business sitting in the address
 * bar, in history, or in a screenshot of either.
 */

const STORAGE_KEY = "family.invite";

/**
 * Long enough for any code the backend mints (six), slack enough that changing
 * `CODE_LENGTH` there does not silently truncate here. It is not a validity
 * check — the backend judges that — only a ceiling, so a hostile link cannot
 * put an essay into session storage and into the field.
 */
const MAX_CODE_LENGTH = 16;

/** The normalisation `households.normalizeCode` applies, so what is shown is what is sent. */
function normalize(code: string) {
  return code
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, MAX_CODE_LENGTH);
}

/** The `?code=` of a query string, normalised, or null if there isn't a usable one. */
export function inviteFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get("code");
  if (!raw) return null;
  return normalize(raw) || null;
}

/** Only the three methods used, so a test can hand this a plain object. */
export type CodeStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * The code for this arrival: the URL's if it has one, otherwise whatever an
 * earlier load of this tab put away.
 *
 * The URL wins, so opening a second invite link replaces the first rather than
 * joining with a code the person has since been sent a replacement for.
 *
 * Exported for the tests. `main.tsx` gets the module-scope call below.
 */
export function capturePendingInvite(search: string, store: CodeStore | null): string | null {
  const fromUrl = inviteFromSearch(search);

  if (fromUrl) {
    try {
      store?.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // Storage full, or blocked. The value is still returned and held in
      // memory, which covers everything except a reload — losing the code to a
      // reload is much better than losing the sign-in to a thrown error.
    }
    return fromUrl;
  }

  try {
    return store?.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * `sessionStorage` throws rather than returning undefined when it is blocked,
 * so reaching it is itself a guarded operation.
 */
function sessionStore(): CodeStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * The capture, and the reason this module is imported for its side effect.
 *
 * The `window` guard is not about SSR — there is none — but about the unit
 * tests, which import this file under Node to exercise the functions above.
 */
let pending =
  typeof window === "undefined" ? null : capturePendingInvite(window.location.search, sessionStore());

/** The code this arrival should join with, or null to ask for one. */
export function pendingInvite(): string | null {
  return pending;
}

/**
 * Spend it. Called when the join is *attempted*, not when it succeeds: the code
 * is single-use, so a failed attempt has burned it either way, and clearing on
 * success only would leave a reload retrying a dead code forever. The join
 * screen keeps the value in its field, so a retry is still one tap.
 */
export function clearPendingInvite() {
  pending = null;
  try {
    sessionStore()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: it is already gone from memory, which is what is read.
  }
}
